import React, {FC, useEffect, useState} from 'react';

import {AppButton} from '@/components/Button';
import stringUtils from '@/utils/stringUtils';
import {twx} from '@/utils/tailwindUtils';

export interface CooldownButtonProps extends Omit<React.ComponentPropsWithoutRef<'button'>, 'onClick'> {
    cooldownSeconds: number;
    className?: string;
    label: string;
    onClick?: (event: React.MouseEvent<HTMLElement>) => void;
    runningLabel?: string;
    disabled?: boolean;
    loading?: boolean;
    startInCooldown?: boolean;
    tertiaryNoActive?: boolean;
}

const CooldownButton: FC<CooldownButtonProps> = ({
    cooldownSeconds,
    className,
    label,
    onClick,
    runningLabel,
    disabled,
    loading,
    startInCooldown,
    ...rest
}) => {
    const [remainingSeconds, setRemainingSeconds] = useState<number>(0);
    const [endTime, setEndTime] = useState<number | null>(() =>
        startInCooldown && cooldownSeconds > 0 ? Date.now() + cooldownSeconds * 1000 : null
    );

    useEffect(() => {
        if (startInCooldown && cooldownSeconds > 0) {
            setEndTime(Date.now() + cooldownSeconds * 1000);
            return;
        }

        if (!startInCooldown) {
            setEndTime(null);
            setRemainingSeconds(0);
        }
    }, [startInCooldown, cooldownSeconds]);

    useEffect(() => {
        if (!endTime) {
            setRemainingSeconds(0);
            return;
        }

        const updateRemaining = () => {
            const now = Date.now();
            const diff = endTime - now;

            if (diff <= 0) {
                setRemainingSeconds(0);
                setEndTime(null);
                return;
            }

            setRemainingSeconds(Math.ceil(diff / 1000));
        };

        updateRemaining();

        const intervalId = setInterval(updateRemaining, 1000);

        return () => clearInterval(intervalId);
    }, [endTime]);

    const isInCooldown = remainingSeconds > 0;
    const isDisabled = Boolean(disabled || loading || isInCooldown);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        if (isDisabled) {
            return;
        }

        onClick?.(event);

        if (cooldownSeconds > 0) {
            setEndTime(Date.now() + cooldownSeconds * 1000);
        } else {
            setEndTime(null);
            setRemainingSeconds(0);
        }
    };

    return (
        <AppButton
            tertiary
            className={twx('mx-auto', className)}
            disabled={isDisabled}
            onClick={handleClick}
            loading={loading}
            {...rest}
        >
            {isInCooldown && runningLabel ? stringUtils.formatString(runningLabel, remainingSeconds) : label}
        </AppButton>
    );
};

export default CooldownButton;
